import React, { createContext, useContext, useState } from 'react';

// ── Context ───────────────────────────────────────────────────────────────

interface TabContextValue {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabContext = createContext<TabContextValue>({ activeTab: '', setActiveTab: () => {} });

// ── TabPanelContainer ─────────────────────────────────────────────────────

export interface TabPanelContainerProps {
  /** Default active tab id. Falls back to the first child's id. */
  defaultTab?: string;
  /** Controlled active tab id. */
  activeTab?: string;
  /** Called when the user clicks a tab. */
  onTabChange?: (id: string) => void;
  children: React.ReactNode;
  /** Accent color for the active tab indicator (defaults to #6366f1). */
  accentColor?: string;
  /**
   * `horizontal`: tab buttons in a row (default).
   * `vertical`: tab buttons stacked on the left, panel content on the right.
   */
  orientation?: 'horizontal' | 'vertical';
  /** Extra style applied to the outer wrapper. */
  style?: React.CSSProperties;
  /** Merged into the panel content wrapper (area to the right of vertical tabs). */
  panelAreaStyle?: React.CSSProperties;
}

function isTabPanelElement(child: React.ReactNode): child is React.ReactElement<TabPanelProps> {
  if (!React.isValidElement(child)) return false;

  const type = child.type as { __isTabPanel?: boolean; displayName?: string };
  if (type?.__isTabPanel || type?.displayName === 'ProfaseTabPanel') {
    return true;
  }

  const props = child.props as Partial<TabPanelProps>;
  return typeof props.id === 'string' && props.label !== undefined;
}

export function TabPanelContainer({
  defaultTab,
  activeTab: controlledTab,
  onTabChange,
  children,
  accentColor = '#6366f1',
  orientation = 'horizontal',
  style,
  panelAreaStyle,
}: TabPanelContainerProps) {
  // Collect tab descriptors from children
  const tabs: { id: string; label: React.ReactNode; selectable: boolean }[] = [];
  React.Children.forEach(children, child => {
    if (isTabPanelElement(child)) {
      const p = child.props as TabPanelProps;
      tabs.push({ id: p.id, label: p.label, selectable: p.selectable !== false });
    }
  });

  const fallbackDefault = tabs[0]?.id ?? '';
  const [internalTab, setInternalTab] = useState(defaultTab ?? fallbackDefault);

  const isControlled = controlledTab !== undefined;
  const activeTab    = isControlled ? controlledTab! : internalTab;

  const handleSelect = (id: string) => {
    if (!isControlled) setInternalTab(id);
    onTabChange?.(id);
  };

  const isVertical = orientation === 'vertical';

  return (
    <TabContext.Provider value={{ activeTab, setActiveTab: handleSelect }}>
      <div
        style={{
          display: 'flex',
          flexDirection: isVertical ? 'row' : 'column',
          gap: isVertical ? 12 : undefined,
          alignItems: isVertical ? 'stretch' : undefined,
          ...style,
        }}
      >
        {/* Tab bar */}
        <div style={isVertical ? s.tabBarVertical : s.tabBar}>
          {tabs.map(tab => {
            const isActive   = tab.id === activeTab;
            const isDisabled = !tab.selectable && !isActive;
            return (
              <button
                key={tab.id}
                type="button"
                disabled={isDisabled}
                onClick={() => !isDisabled && handleSelect(tab.id)}
                className={[
                  'profase-tab-btn',
                  isVertical ? 'profase-tab-btn--vertical' : '',
                  isActive ? 'profase-tab-btn--active' : '',
                  isDisabled ? 'profase-tab-btn--disabled' : '',
                ].filter(Boolean).join(' ')}
                style={isActive ? { background: accentColor } : undefined}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Panels */}
        <div
          style={{
            ...s.panelArea,
            ...(isVertical ? { minWidth: 0, minHeight: 0 } : {}),
            ...panelAreaStyle,
          }}
        >
          {children}
        </div>

      </div>
    </TabContext.Provider>
  );
}

// ── TabPanel ──────────────────────────────────────────────────────────────

export interface TabPanelProps {
  /** Must be unique within its container. */
  id: string;
  /** Text or node shown in the tab bar button. */
  label: React.ReactNode;
  children?: React.ReactNode;
  /** Extra style applied to the panel wrapper when visible. */
  style?: React.CSSProperties;
  /**
   * Whether the user can click the tab button to select it.
   * Defaults to true. When false, the tab can only be activated
   * programmatically (e.g. via useTabPanel / TabAutoSwitch).
   */
  selectable?: boolean;
}

export function TabPanel({ id, children, style }: TabPanelProps) {
  const { activeTab } = useContext(TabContext);
  if (activeTab !== id) return null;
  return <div style={{ ...s.panel, ...style }}>{children}</div>;
}

TabPanel.displayName = 'ProfaseTabPanel';
(TabPanel as any).__isTabPanel = true;

// ── Hook ──────────────────────────────────────────────────────────────────

/** Programmatically switch tabs from inside a TabPanelContainer. */
export function useTabPanel() {
  return useContext(TabContext);
}

// ── Styles ────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  tabBar: {
    display: 'flex', flexWrap: 'wrap', gap: 4,
    marginBottom: 8,
  },
  tabBarVertical: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    gap: 4,
    flex: '0 0 auto',
    minWidth: 172,
    maxWidth: 228,
    marginBottom: 0,
    alignSelf: 'stretch',
  },
  panelArea: {
    flex: 1,
  },
  panel: {
    padding: '12px 0',
  },
};
